import React, { useState } from 'react';
import { Form, Field } from 'react-final-form';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import css from './Step2Contact.module.css';

const LANGUAGES = [
  'English', 'Mandarin Chinese', 'Cantonese', 'Spanish', 'French',
  'German', 'Japanese', 'Korean', 'Arabic', 'Portuguese',
  'Russian', 'Hindi', 'Italian', 'Dutch', 'Turkish',
];

const AgentStep2Contact = ({ nextStep, prevStep, initialValues }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const validate = values => {
    const errors = {};
    if (!values.contactPersonName?.trim()) errors.contactPersonName = 'Contact name is required';
    if (!values.phone?.trim()) errors.phone = 'Phone number is required';
    if (!values.email?.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) {
      errors.email = 'Enter a valid email address';
    }
    if (!values.languages || values.languages.length === 0) {
      errors.languages = 'Select at least one language';
    }
    if (!values.password) {
      errors.password = 'Password is required';
    } else if (values.password.length < 8) {
      errors.password = 'Minimum 8 characters';
    }
    if (!values.confirmPassword) {
      errors.confirmPassword = 'Please confirm your password';
    } else if (values.password !== values.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }
    return errors;
  };

  const onSubmit = values => {
    const { confirmPassword, ...rest } = values;
    nextStep(rest);
  };

  return (
    <Form
      onSubmit={onSubmit}
      validate={validate}
      initialValues={initialValues}
      render={({ handleSubmit, values, form }) => (
        <form onSubmit={handleSubmit} className={css.form}>

          <div className={css.header}>
            <span className={css.step}>Step 2 of 5</span>
            <h2 className={css.title}>Contact & Account Setup</h2>
            <p className={css.description}>Add the primary contact details and set your account password.</p>
          </div>

          <hr className={css.divider} />

          <section className={css.section}>
            <div className={css.flex}>
              <div className={css.field}>
                <label className={css.label}>
                  Contact Person Name <span className={css.required}>*</span>
                </label>
                <Field name="contactPersonName">
                  {({ input, meta }) => (
                    <>
                      <input
                        {...input}
                        className={classNames(css.input, { [css.inputError]: meta.touched && meta.error })}
                        placeholder="Full name"
                      />
                      {meta.touched && meta.error && <span className={css.error}>{meta.error}</span>}
                    </>
                  )}
                </Field>
              </div>

              <div className={css.field}>
                <label className={css.label}>
                  Phone Number <span className={css.required}>*</span>
                </label>
                <Field name="phone">
                  {({ input, meta }) => (
                    <>
                      <input
                        {...input}
                        type="tel"
                        className={classNames(css.input, { [css.inputError]: meta.touched && meta.error })}
                        placeholder="+1 234 567 8900"
                      />
                      {meta.touched && meta.error && <span className={css.error}>{meta.error}</span>}
                    </>
                  )}
                </Field>
              </div>
            </div>

            <div className={css.fieldFull} style={{ marginBottom: 18 }}>
              <label className={css.label}>
                Email Address <span className={css.required}>*</span>
              </label>
              <Field name="email">
                {({ input, meta }) => (
                  <>
                    <input
                      {...input}
                      type="email"
                      className={classNames(css.input, { [css.inputError]: meta.touched && meta.error })}
                      placeholder="you@example.com"
                    />
                    {meta.touched && meta.error && <span className={css.error}>{meta.error}</span>}
                  </>
                )}
              </Field>
            </div>

            {/* Languages */}
            <div className={css.fieldFull} style={{ marginBottom: 18 }}>
              <label className={css.label}>
                Languages Spoken <span className={css.required}>*</span>
              </label>
              <Field name="languages">
                {({ meta }) => (
                  <>
                    <div style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '8px',
                      padding: '14px',
                      background: '#f8fafc',
                      border: `1.5px solid ${meta.touched && meta.error ? '#ef4444' : '#e2e8f0'}`,
                      borderRadius: '12px',
                    }}>
                      {LANGUAGES.map(lang => {
                        const selected = (values.languages || []).includes(lang);
                        return (
                          <button
                            key={lang}
                            type="button"
                            onClick={() => {
                              const current = values.languages || [];
                              const next = selected
                                ? current.filter(l => l !== lang)
                                : [...current, lang];
                              form.change('languages', next);
                            }}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 6,
                              padding: '6px 14px',
                              borderRadius: 999,
                              border: `1.5px solid ${selected ? '#2563eb' : '#cbd5e1'}`,
                              background: selected ? 'linear-gradient(135deg,#2563eb,#1d4ed8)' : '#ffffff',
                              color: selected ? '#ffffff' : '#475569',
                              fontSize: 13,
                              fontWeight: selected ? 700 : 500,
                              cursor: 'pointer',
                              transition: 'all 0.18s',
                              boxShadow: selected ? '0 2px 8px rgba(37,99,235,0.25)' : 'none',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {selected && <span style={{ fontSize: 11 }}>✓</span>}
                            {lang}
                          </button>
                        );
                      })}
                    </div>
                    {(values.languages || []).length > 0 && (
                      <div style={{ fontSize: 12, color: '#2563eb', fontWeight: 600, marginTop: 6 }}>
                        {(values.languages || []).length} language{(values.languages || []).length !== 1 ? 's' : ''} selected
                      </div>
                    )}
                    {meta.touched && meta.error && <span className={css.error}>{meta.error}</span>}
                  </>
                )}
              </Field>
            </div>

            <div className={css.flex}>
              <div className={css.field}>
                <label className={css.label}>
                  Password <span className={css.required}>*</span>
                </label>
                <Field name="password">
                  {({ input, meta }) => (
                    <>
                      <div className={css.passwordBox}>
                        <input
                          {...input}
                          type={showPassword ? 'text' : 'password'}
                          className={classNames(css.input, { [css.inputError]: meta.touched && meta.error })}
                          placeholder="Min. 8 characters"
                        />
                        <button type="button" className={css.eyeBtn} onClick={() => setShowPassword(v => !v)}>
                          {showPassword ? <FiEyeOff /> : <FiEye />}
                        </button>
                      </div>
                      {meta.touched && meta.error && <span className={css.error}>{meta.error}</span>}
                    </>
                  )}
                </Field>
              </div>

              <div className={css.field}>
                <label className={css.label}>
                  Confirm Password <span className={css.required}>*</span>
                </label>
                <Field name="confirmPassword">
                  {({ input, meta }) => (
                    <>
                      <div className={css.passwordBox}>
                        <input
                          {...input}
                          type={showConfirm ? 'text' : 'password'}
                          className={classNames(css.input, { [css.inputError]: meta.touched && meta.error })}
                          placeholder="Re-enter password"
                        />
                        <button type="button" className={css.eyeBtn} onClick={() => setShowConfirm(v => !v)}>
                          {showConfirm ? <FiEyeOff /> : <FiEye />}
                        </button>
                      </div>
                      {meta.touched && meta.error && <span className={css.error}>{meta.error}</span>}
                    </>
                  )}
                </Field>
              </div>
            </div>
          </section>

          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 28, gap: 12 }}>
            <button
              type="button"
              onClick={prevStep}
              style={{
                padding: '13px 24px',
                border: '1.5px solid #e2e8f0',
                borderRadius: 12,
                background: '#ffffff',
                color: '#475569',
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
            >
              ← Previous
            </button>
            <button
              type="submit"
              style={{
                padding: '13px 28px',
                border: 'none',
                borderRadius: 12,
                background: 'linear-gradient(135deg,#2563eb,#1d4ed8)',
                color: '#ffffff',
                fontSize: 14,
                fontWeight: 700,
                cursor: 'pointer',
                boxShadow: '0 4px 14px rgba(37,99,235,0.3)',
                transition: 'all 0.25s',
              }}
            >
              Next : Services & Capabilities →
            </button>
          </div>

        </form>
      )}
    />
  );
};

AgentStep2Contact.defaultProps = { initialValues: {} };
AgentStep2Contact.propTypes = {
  nextStep: PropTypes.func.isRequired,
  prevStep: PropTypes.func.isRequired,
  initialValues: PropTypes.object,
};

export default AgentStep2Contact;
